import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export function TextInput({ label, icon, placeholder, value, name, onChange, disabled }:
    { label?: any, icon?: any, placeholder?: any, value: any, name?: any, onChange: any, disabled?: any }) {
    return (
        <div className={`mb-4`}>
            {label &&
                <label className="block text-sm font-medium mb-1">{label}</label>
            }
            <div className={`flex rounded items-center border border-gray-200 py-2 px-3 bg-white`}>
                {icon}
                <input
                    type={'text'}
                    name={name}
                    className={`appearance-none border-none bg-transparent w-full text-gray-700 py-0.5 px-2 
                    leading-tight focus:outline-none md:text-sm placeholder:text-sm
                    
                    `}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                />
            </div>
        </div>
    )
}

export function PasswordInput({ label, icon, placeholder, value, name, onChange, disabled }:
    { label?: any, icon?: any, placeholder?: any, value: any, name?: any, onChange: any, disabled?: any }) {
    return (
        <div className={`mb-4 `}>
            {label &&
                <label className="block text-sm font-medium mb-1">{label}</label>
            }
            <div className={`flex rounded items-center border border-gray-200 py-2 bg-white`}>
                {icon}
                <input
                    type={'password'}
                    name={name}
                    className={`appearance-none border-none bg-transparent w-full text-gray-700 py-0.5 px-2 
                    leading-tight focus:outline-none md:text-sm placeholder:text-sm
                    
                    `}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                />
            </div>
        </div>
    )
}

export function Selectinput({ label, onChange, className, name, value, options }: { options: any; value: string; name: string, label: string, onChange: any, className?: string }) {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleToggle = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const dropdownHeight = 240; // max-h-60 = 240px
            
            // Open upward if there's not enough space below but enough space above
            setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
        }
        setIsOpen(!isOpen);
        if (!isOpen) {
            // Reset search when opening
            setSearchTerm("");
        }
    };

    const selectedOption = options.find((opt: any) => opt.value === value) || options[0];
    const displayValue = selectedOption?.label || 'Select an option';

    const handleSelect = (optionValue: string) => {
        const syntheticEvent = {
            target: {
                name,
                value: optionValue
            }
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(syntheticEvent);
        setIsOpen(false);
        setSearchTerm("");
    };

    // Filter options based on search term
    const filteredOptions = options.filter((option: any) => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className={`mb-4 ${className}`} ref={dropdownRef}>
            {label &&
                <label className="block text-sm font-medium mb-1">{label}</label>
            }
            <div className="relative">
                <button
                    ref={buttonRef}
                    type="button"
                    onClick={handleToggle}
                    className="flex rounded items-center justify-between border border-gray-200 py-2 px-3 bg-white w-full text-left hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                >
                    <span className={`text-gray-700 md:text-sm ${!value ? 'text-gray-400' : ''}`}>
                        {displayValue}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
                </button>
                {isOpen && (
                    <div className={`absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden flex flex-col ${
                        openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
                    }`}>
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Search..."
                            className="px-3 py-2 border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <div className="overflow-auto max-h-48">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option: any) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => handleSelect(option.value)}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                            value === option.value 
                                                ? 'bg-blue-50 text-blue-700 font-medium' 
                                                : 'text-gray-700'
                                        } ${option.value === '' ? 'text-gray-400' : ''}`}
                                    >
                                        {option.label}
                                    </button>
                                ))
                            ) : (
                                <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}


